import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { UserEvent, isUserEvent } from './events';

/**
 * Global EventSource for per-user real-time events.
 *
 * One persistent connection per browser tab. The server pushes JSON
 * frames; we parse and dispatch into the lightweight store exposed via
 * `useUserEvents()`. Auto-reconnect is built in.
 *
 * Mount once at the app root (wraps `<App />` in main.tsx). Components
 * subscribe with `useUserEvents()` to react to events.
 */

/** Callback a UserEvent. Used by subscribers. */
type UserEventHandler = (event: UserEvent) => void;

/** subscribe signature: take a handler, return an unsubscribe function. */
type SubscribeFn = (handler: UserEventHandler) => () => void;

interface UserEventsContextValue {
  /**
   * Subscribe to all incoming events. Returns an unsubscribe function.
   */
  subscribe: SubscribeFn;

  /**
   * Most recent event (any type). Useful for triggering refetches or
   * toasts when "anything happened".
   */
  lastEvent: UserEvent | null;

  /**
   * Whether the SSE connection is currently open. Useful for showing
   * a "live" indicator or a reconnecting banner.
   */
  connected: boolean;
}

const UserEventsContext = createContext<UserEventsContextValue | null>(null);

/**
 * Internal hook. Components should use `useUserEvents()` instead.
 */
function useUserEventsContext(): UserEventsContextValue {
  const ctx = useContext(UserEventsContext);
  if (!ctx) {
    throw new Error('useUserEvents must be used inside <UserEventsProvider>');
  }
  return ctx;
}

/**
 * Subscribes to ALL incoming events of a given type.
 *
 * Example:
 *   useEvent('cash.updated', (e) => setCash(e.new_cash));
 *
 * Implementation note: the handler is stored in a ref so we only subscribe
 * once per (subscribe, type) tuple. Without the ref, every parent render
 * would tear down and re-create the subscription, creating a brief window
 * where no listener is registered — and any event arriving in that window
 * would be dropped.
 */
export function useEvent<T extends UserEvent['type']>(
  type: T,
  handler: (event: Extract<UserEvent, { type: T }>) => void
): void {
  const { subscribe } = useUserEventsContext();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const unsub = subscribe((event) => {
      if (event.type === type) {
        handlerRef.current(event as Extract<UserEvent, { type: T }>);
      }
    });
    return unsub;
    // `handler` is intentionally excluded — we read it from the ref so
    // a new function identity each render doesn't re-subscribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, type]);
}

/**
 * Subscribes to all events. Use this when you need to react to multiple
 * types from a single component.
 */
export function useUserEvents(): UserEventsContextValue {
  return useUserEventsContext();
}

export function UserEventsProvider({ children }: { children: ReactNode }) {
  const listenersRef = useRef<Set<UserEventHandler>>(new Set());
  const [lastEvent, setLastEvent] = useState<UserEvent | null>(null);
  const [connected, setConnected] = useState(false);

  // Stable subscribe function that doesn't change identity across renders.
  const subscribeRef = useRef<SubscribeFn>((handler) => {
    listenersRef.current.add(handler);
    return () => {
      listenersRef.current.delete(handler);
    };
  });

  useEffect(() => {
    const url = `${import.meta.env.VITE_BACKEND_URL}/api/users/me/events`;
    const source = new EventSource(url, { withCredentials: true });

    source.onopen = () => setConnected(true);
    source.onerror = () => {
      // EventSource auto-reconnects; only mark disconnected if the
      // server explicitly closed the stream.
      if (source.readyState === EventSource.CLOSED) {
        setConnected(false);
      }
    };
    source.onmessage = (e) => {
      try {
        const parsed: unknown = JSON.parse(e.data);
        if (!isUserEvent(parsed)) return;
        setLastEvent(parsed);
        for (const listener of listenersRef.current) {
          try {
            listener(parsed);
          } catch {
            // a bad subscriber should not break the others
          }
        }
      } catch {
        // Ignore malformed frames; the next tick will replace them.
      }
    };

    return () => {
      source.close();
      setConnected(false);
    };
  }, []);

  const value: UserEventsContextValue = {
    subscribe: subscribeRef.current,
    lastEvent,
    connected,
  };

  return (
    <UserEventsContext.Provider value={value}>
      {children}
    </UserEventsContext.Provider>
  );
}